import { useCallback, useEffect, useMemo, useState } from "react";
import { formatApiError } from "@/lib/api";
import {
  fetchExportPeriodCount,
  fetchExportZipBlob,
  triggerBlobDownload,
} from "@/lib/exportDirectorioApi";
import { assessExportBeforeGenerate } from "@/lib/exportPeriodLimits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Download, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DIRECCION_OPTIONS = [
  { value: "todos", label: "Emitidos y recibidos" },
  { value: "emitido", label: "Solo emitidos" },
  { value: "recibido", label: "Solo recibidos" },
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function monthStartIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function mapListDireccionToExport(direccion) {
  const d = (direccion || "").trim().toLowerCase();
  if (d === "emitido" || d === "recibido") return d;
  return "todos";
}

function exportDireccionParam(value) {
  const v = (value || "").trim().toLowerCase();
  if (v === "emitido" || v === "recibido") return v;
  return undefined;
}

function direccionLabel(value) {
  return DIRECCION_OPTIONS.find((o) => o.value === value)?.label || value;
}

function filterMissingMonthsInRange(missingMonths, fechaInicio, fechaFin) {
  const fromM = String(fechaInicio || "").slice(0, 7);
  const toM = String(fechaFin || "").slice(0, 7);
  if (!fromM || !toM) return [];
  return (missingMonths || []).filter((m) => {
    const ym = String(m.month || "").slice(0, 7);
    return ym && ym >= fromM && ym <= toM;
  });
}

function formatGapMonthsLabel(months, max = 8) {
  const labels = months.slice(0, max).map((m) => m.label || m.month);
  if (months.length > max) labels.push("…");
  return labels.join(", ");
}

function evaluateExportPreview({ countStats, gapsInRange, includePdf, direccion, fechaInicio, fechaFin }) {
  const exportable = Number(countStats?.exportable ?? countStats?.total ?? 0) || 0;
  const visible = Number(countStats?.visible ?? 0) || 0;
  const sinXml = Number(countStats?.sin_xml ?? 0) || 0;
  const volumeCheck = assessExportBeforeGenerate({
    count: exportable,
    includePdf,
    dirScope: direccion === "todos" ? "todos" : direccion,
    fechaInicio,
    fechaFin,
  });

  let status = "ready";
  if (volumeCheck.level === "block") status = "blocked_volume";
  else if (exportable === 0 && visible === 0) status = "blocked_empty";
  else if (exportable === 0 && visible > 0) status = "blocked_no_xml";
  else if (exportable > 0 && gapsInRange.length > 0) status = "partial";

  return { status, exportable, visible, sinXml, volumeCheck };
}

/**
 * Exportación ZIP desde MongoDB (sin SAT). Detalle de conteo solo al confirmar descarga.
 */
export default function AccountingZipExportPanel({
  clientId,
  clientRfc,
  appliedFilters,
  dashboard,
  disabled = false,
}) {
  const [fechaInicio, setFechaInicio] = useState(monthStartIso);
  const [fechaFin, setFechaFin] = useState(todayIso);
  const [direccion, setDireccion] = useState("todos");
  const [includePdf, setIncludePdf] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPreview, setConfirmPreview] = useState(null);

  useEffect(() => {
    setFechaInicio(appliedFilters?.fechaDesde || monthStartIso());
    setFechaFin(appliedFilters?.fechaHasta || todayIso());
    setDireccion(mapListDireccionToExport(appliedFilters?.direccion));
  }, [
    appliedFilters?.fechaDesde,
    appliedFilters?.fechaHasta,
    appliedFilters?.direccion,
  ]);

  const periodReady = Boolean(fechaInicio && fechaFin && fechaFin >= fechaInicio);

  const gapsInRange = useMemo(
    () => filterMissingMonthsInRange(dashboard?.missing_months, fechaInicio, fechaFin),
    [dashboard?.missing_months, fechaInicio, fechaFin],
  );

  const buildZipParams = useCallback(() => {
    const params = {
      client_id: clientId,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      include_pdf: includePdf,
      align_visor: true,
    };
    const d = exportDireccionParam(direccion);
    if (d) params.direccion = d;
    return params;
  }, [clientId, fechaInicio, fechaFin, includePdf, direccion]);

  const fetchPreview = useCallback(async () => {
    const params = {
      client_id: clientId,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      align_visor: true,
    };
    const d = exportDireccionParam(direccion);
    if (d) params.direccion = d;
    return fetchExportPeriodCount(params);
  }, [clientId, fechaInicio, fechaFin, direccion]);

  const runZipDownload = async () => {
    if (!clientId || zipBusy) return;
    setZipBusy(true);
    try {
      const { blob, filename } = await fetchExportZipBlob(buildZipParams());
      triggerBlobDownload(blob, filename);
      const n = confirmPreview?.exportable ?? 0;
      const partialNote =
        confirmPreview?.status === "partial" && gapsInRange.length > 0
          ? ` Incluye ${n.toLocaleString("es-MX")} XML. Meses sin datos en BD: ${formatGapMonthsLabel(gapsInRange)}.`
          : ` ${n.toLocaleString("es-MX")} XML exportados desde la base de datos.`;
      toast.success(`Exportación terminada: ${filename}.${partialNote}`, { duration: 12000 });
    } catch (e) {
      const detail = e?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      const msg =
        code === "EXPORT_NO_XML_ON_DISK"
          ? detail?.message ||
            "Hay CFDI registrados pero sin archivo XML. Complete la integración antes de exportar."
          : code === "EXPORT_EMPTY_PERIOD"
            ? "No hay comprobantes en la base de datos para ese periodo y filtro."
            : formatApiError(detail) || e?.message || "No se pudo generar el ZIP";
      toast.error(msg, { duration: 14000 });
    } finally {
      setZipBusy(false);
      setConfirmOpen(false);
      setConfirmPreview(null);
    }
  };

  const onDownloadClick = async () => {
    if (!clientId || !periodReady || disabled || zipBusy) return;
    setZipBusy(true);
    try {
      const countStats = await fetchPreview();
      const preview = evaluateExportPreview({
        countStats,
        gapsInRange,
        includePdf,
        direccion,
        fechaInicio,
        fechaFin,
      });

      if (preview.status === "blocked_empty") {
        toast.error(
          "No hay comprobantes en la base de datos para ese periodo. Active la sincronización o cargue XML antes de exportar.",
          { duration: 12000 },
        );
        return;
      }
      if (preview.status === "blocked_no_xml") {
        toast.error(
          `Hay ${preview.visible.toLocaleString("es-MX")} CFDI registrados, pero ninguno tiene XML disponible. Complete la integración antes de exportar.`,
          { duration: 12000 },
        );
        return;
      }
      if (preview.status === "blocked_volume") {
        toast.error(preview.volumeCheck?.message || "Periodo o volumen demasiado grande.", {
          duration: 14000,
        });
        return;
      }

      setConfirmPreview(preview);
      setConfirmOpen(true);
    } catch {
      toast.error("No se pudo verificar el periodo en la base de datos.");
    } finally {
      setZipBusy(false);
    }
  };

  const confirmTitle =
    confirmPreview?.status === "partial" ? "Exportación parcial" : "Confirmar descarga ZIP";

  const canClickDownload = Boolean(clientId && periodReady && !disabled && !zipBusy);

  return (
    <div
      className="rounded-md border border-slate-200 bg-white p-4 space-y-3"
      data-testid="accounting-zip-export-panel"
    >
      <div className="flex items-start gap-2">
        <Info className="h-4 w-4 text-sky-700 shrink-0 mt-0.5" aria-hidden />
        <div>
          <p className="text-sm font-semibold text-[#0F2D5C] m-0">Exportar comprobantes (ZIP)</p>
          <p className="text-xs text-slate-600 m-0 mt-1 leading-relaxed">
            Solo empaqueta XML ya almacenados en su base de datos.{" "}
            <strong>No consulta ni descarga del SAT.</strong>
            {includePdf ? " Los PDF se generan localmente desde el XML guardado." : null}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Desde</Label>
          <Input
            type="date"
            className="mt-1 rounded-sm w-36 h-8 text-xs"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            data-testid="accounting-export-desde"
          />
        </div>
        <div>
          <Label className="text-xs">Hasta</Label>
          <Input
            type="date"
            className="mt-1 rounded-sm w-36 h-8 text-xs"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            data-testid="accounting-export-hasta"
          />
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs">Tipo</Label>
          <Select value={direccion} onValueChange={setDireccion}>
            <SelectTrigger className="mt-1 rounded-sm h-8 text-xs" data-testid="accounting-export-direccion">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DIRECCION_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer pb-1">
          <Checkbox
            checked={includePdf}
            onCheckedChange={(v) => setIncludePdf(v === true)}
            data-testid="accounting-export-include-pdf"
          />
          Incluir PDF (generado local)
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          className="h-8 rounded-sm bg-[#0F2D5C] hover:bg-[#0F2D5C]/90"
          disabled={!canClickDownload}
          onClick={onDownloadClick}
          data-testid="accounting-export-zip-btn"
        >
          {zipBusy ? (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5 mr-1.5" />
          )}
          Descargar ZIP
        </Button>
        {clientRfc ? (
          <span className="text-[11px] text-slate-500 font-mono">RFC {clientRfc}</span>
        ) : null}
      </div>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          setConfirmOpen(open);
          if (!open) setConfirmPreview(null);
        }}
      >
        <AlertDialogContent data-testid="accounting-export-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmTitle}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                {confirmPreview ? (
                  <>
                    <p className="m-0">
                      Se incluirán{" "}
                      <strong>{confirmPreview.exportable.toLocaleString("es-MX")} XML</strong>
                      {includePdf ? " con PDF generado localmente" : ""} del{" "}
                      <strong>{fechaInicio}</strong> al <strong>{fechaFin}</strong> (
                      {direccionLabel(direccion)}).
                    </p>
                    <p className="m-0 text-xs text-slate-500">
                      Solo documentos ya guardados en la base de datos; no se consultará el SAT.
                    </p>
                    {confirmPreview.status === "partial" && gapsInRange.length > 0 ? (
                      <p className="m-0 text-amber-900">
                        <strong>Atención:</strong> en ese rango faltan meses sin datos en BD:{" "}
                        {formatGapMonthsLabel(gapsInRange, 10)}. El ZIP solo contendrá los meses con
                        información descargada.
                      </p>
                    ) : null}
                    {confirmPreview.volumeCheck?.level === "warn" ? (
                      <p className="m-0 text-amber-900">{confirmPreview.volumeCheck.message}</p>
                    ) : null}
                  </>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#0F2D5C] hover:bg-[#0F2D5C]/90"
              onClick={(e) => {
                e.preventDefault();
                runZipDownload();
              }}
            >
              Descargar ZIP
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
